import React from 'react';
import MissionCard from '../components/MissionCard/MissionCard';
import missions from '../data/missions.json';

import '../styles/global.css';

const researchMissions = missions.filter(({ role }) =>
  role.toLowerCase().includes('research')
);

const researchLinks = [
  {
    label: 'Scientific articles on ArXiv',
    href: 'https://arxiv.org/search/math?query=Ya%C5%9Far%2C+Firat&searchtype=author&abstracts=show&order=-announced_date_first&size=50',
  },
  {
    label: 'Scientific genealogy',
    href: 'https://www.genealogy.math.ndsu.nodak.edu/id.php?id=282944',
  },
];

const mathematicsTalks = [
  {
    venue: 'Universite de Strasbourg - IRMA',
    event: 'Seminaire Doctorants',
    location: 'Strasbourg, France',
    title: 'Teichmuller Spaces on Surfaces of Infinite Type',
  },
  {
    venue: 'Universite de Strasbourg - IRMA',
    event: 'Seminaire GT3',
    location: 'Strasbourg, France',
    date: 'June 6, 2018',
    title: 'Hyperbolic Structures on Surfaces of Infinite Type',
  },
  {
    venue: 'Universitat Heidelberg',
    event: 'The 10th Heidelberg-Karlsruhe-Strasbourg Geometry Day',
    location: 'Heidelberg, Germany',
    date: 'December 1, 2017',
    title: 'Riemann Surfaces and Line Complexes',
  },
  {
    venue: 'Universite de Strasbourg - IRMA',
    event: 'Seminaire GT3',
    location: 'Strasbourg, France',
    title: 'Riemann Surfaces and Complex Functions',
  },
  {
    venue: 'CIRM - Luminy, Centre International de Rencontres Mathematiques',
    event: 'Theorie de Teichmuller : classique, superieure, super et quantique',
    location: 'Marseille, France',
    date: 'October 5-9, 2020',
    title: 'Infinite-dimensional Teichmuller spaces',
  },
  {
    venue: 'Universite Haute-Alsace',
    event: "Seminaire Georges Reeb d'Algebre et Geometrie Differentielle",
    location: 'Mulhouse, France',
    date: 'December 8, 2016',
    title: "Grothendieck's Dessins d'Enfants",
  },
  {
    venue: 'Universite de Strasbourg - IRMA',
    event: 'Seminaire GT3',
    location: 'Strasbourg, France',
    title: "The Action of the Group PGL(2,Z) on Dessins d'Enfants",
  },
  {
    venue: 'Universite de Galatasaray',
    event: 'Seminars on Hypergeometric Galois Actions',
    location: 'Istanbul, Turkey',
    title: 'The Modular Group and Primitive Trees',
  },
];

const aiSoftwareTalks = [
  {
    venue: 'Istanbul Bilgi University',
    date: 'May 21, 2025',
    title: 'Behind the scenes: How AI agents work and how ChatGPT responds quickly',
  },
  {
    venue: 'Universite de Galatasaray',
    date: 'May 14, 2025',
    title: 'Agentic generative AI',
  },
];

function TalkCard({ talk }) {
  const meta = [talk.date, talk.location].filter(Boolean);

  return (
    <article className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] p-5 text-left shadow-sm">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[var(--accent-gold)]">
        {meta.map((item, index) => (
          <React.Fragment key={item}>
            {index > 0 && <span aria-hidden="true">/</span>}
            <span>{item}</span>
          </React.Fragment>
        ))}
      </div>
      <h2 className="mt-3 text-lg font-bold leading-tight text-[var(--text-primary)]">
        {talk.title}
      </h2>
      <p className="mt-3 text-sm font-semibold text-[var(--text-secondary)]">
        {talk.venue}
      </p>
      {talk.event && (
        <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">
          {talk.event}
        </p>
      )}
    </article>
  );
}

function ResearchPage() {
  return (
    <div className="page-wrapper">
      <div className="page">
        <section className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
            Research
          </p>
          <h1 className="mt-2 text-3xl font-bold text-[var(--text-primary)] md:text-4xl">
            Hyperbolic geometry and conformal structures
          </h1>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-[var(--text-secondary)]">
            Academic work across Teichmuller theory, conformal geometry, and seminars on
            infinite-type surfaces.
          </p>
        </section>

        <section className="mb-10 grid gap-4 md:grid-cols-2">
          {researchLinks.map((link) => (
            <a
              key={link.href}
              className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-card)] p-5 text-base font-semibold text-[var(--text-primary)] shadow-sm transition hover:-translate-y-0.5 hover:border-[var(--accent-gold)] hover:text-[var(--accent-gold)] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-primary)]"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{link.label}</span>
              <img
                className="h-4 w-4 shrink-0"
                src="/icons/arrow-up-right-square.svg"
                alt=""
                aria-hidden="true"
              />
            </a>
          ))}
        </section>

        <section className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
            Talks on AI & Software Engineering
          </p>
          <div className="mt-4 grid gap-4">
            {aiSoftwareTalks.map((talk) => (
              <TalkCard key={`${talk.venue}-${talk.date}`} talk={talk} />
            ))}
          </div>
        </section>

        <section className="mb-10">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
            Invited Talks on Mathematics
          </p>
          <div className="mt-4 grid gap-4">
            {mathematicsTalks.map((talk) => (
              <TalkCard key={`${talk.venue}-${talk.date}`} talk={talk} />
            ))}
          </div>
        </section>

        <section>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[var(--accent-green)]">
            Academic Background
          </p>
          <div className="mt-4 space-y-6">
            {researchMissions.map((mission) => (
              <MissionCard
                key={`${mission.company}-${mission.role}-${mission.date}`}
                date={mission.date}
                company={mission.company}
                role={mission.role}
                details={mission.details}
                logo={mission.logo}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default ResearchPage;
